import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuthContext } from '@/hooks/AuthContext'
import { Loader2, ShieldX, ArrowLeft, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: string
}

function AccessDeniedPage() {
  const navigate = useNavigate()
  const { user } = useAuthContext()

  const formatRoleName = (role: string) => {
    return role
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
      <Card className="w-full max-w-md border-destructive/20 shadow-lg">
        <CardContent className="pt-8 pb-8">
          <div className="flex flex-col items-center text-center">
            {/* Icon */}
            <div className="mb-6 p-4 rounded-full bg-destructive/10">
              <ShieldX className="h-12 w-12 text-destructive" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold tracking-tight mb-2">
              Access Denied
            </h1>

            {/* Description */}
            <p className="text-muted-foreground mb-6 max-w-sm">
              You don't have the required permissions to access this page.
            </p>

            {/* Current roles */}
            {user?.roles && user.roles.length > 0 && (
              <div className="mb-6 w-full">
                <p className="text-sm text-muted-foreground mb-2">Your current roles:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {user.roles
                    .filter(role => !role.startsWith('default-roles-') && role !== 'offline_access' && role !== 'uma_authorization')
                    .map((role) => (
                      <Badge key={role} variant="outline">
                        {formatRoleName(role)}
                      </Badge>
                    ))}
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="w-full border-t mb-6" />

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
              <Button
                className="flex-1"
                onClick={() => navigate('/studies')}
              >
                <Home className="h-4 w-4 mr-2" />
                Go to Studies
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Background decoration */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-bl from-destructive/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-primary/5 to-transparent rounded-full blur-3xl" />
      </div>
    </div>
  )
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuthContext()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Redirect to login page with the return url
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requiredRole && !hasRole(requiredRole)) {
    // User doesn't have required role - show nice access denied page
    return <AccessDeniedPage />
  }

  return <>{children}</>
}
